import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'antd'
import Dialog from '../../../common/Dialog'
import { getAllCities } from '../../../../utils/locationUtils'
import validateZipcode from '../../../../utils/validateZipcode'
import Actions from '../../../../actions'
import $t from '../../../../i18n'
import { getUrlParams, setUrlParams } from '../../../../utils/useParamUtil'
import useIsMobile from 'hooks/useIsMobile'
import AutocompleteInput from '../../../common/AutocompleteInput'
import './LocationSelector.css'

const LocationSelector = () => {
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const location = useSelector(state => state.providers.providersSearchLocation)
  const supportedStates = useSelector(
    state => state.providers.providersSupportedStates
  )
  const defaultState = useSelector(
    state => state.providers.providersDefaultState
  )

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [newLocation, setNewLocation] = useState('')
  const [filteredOptions, setFilteredOptions] = useState([])
  const [validationError, setValidationError] = useState('')
  const [locationOptions, setLocationOptions] = useState([])
  // const [isDefaultLocation, setIsDefaultLocation] = useState(false)

  const isZipcode = useMemo(() => {
    return /^\d+$/.test(newLocation)
  }, [newLocation])

  useEffect(() => {
    // Rebuilt when the supported states arrive from the app config
    try {
      const cities = getAllCities(supportedStates)
      setLocationOptions(cities)
    } catch (error) {
      console.error('Error loading cities:', error)
      setLocationOptions([])
    }
  }, [supportedStates])

  const handleChangeClick = () => {
    // Convert location object to display string for editing
    let displayValue = ''
    if (location.city && location.state) {
      displayValue = `${location.city}, ${location.state}`
    } else if (location.zipcode) {
      displayValue = location.zipcode
    }

    setNewLocation(displayValue)
    setIsModalOpen(true)
  }

  const isLocationValid = input => {
    const trimmedValue = input?.trim() || ''

    // Only validate if there's a value
    if (trimmedValue) {
      // Check if it's all digits (ZIP code)
      if (/^\d+$/.test(trimmedValue)) {
        const stateCode = validateZipcode(trimmedValue)
        if (!supportedStates.includes(stateCode)) {
          setValidationError(
            $t('searchProviders.locationSelector.invalidZipcode')
          )
          return false
        }
      } else {
        // Check if it's a dropdown option (city, state format)
        const matchingOption = locationOptions.find(option => {
          const rawValue = option.value // e.g., "Columbus,OH"
          const formattedValue = `${option.label}, ${
            option.value.split(',')[1]
          }` // e.g., "Columbus, OH"

          return (
            rawValue === trimmedValue ||
            rawValue.toLowerCase() === trimmedValue.toLowerCase() ||
            formattedValue === trimmedValue ||
            formattedValue.toLowerCase() === trimmedValue.toLowerCase()
          )
        })

        if (!matchingOption) {
          setValidationError(
            $t('searchProviders.locationSelector.invalidLocation')
          )
          return false
        }
      }
    } else {
      setValidationError($t('searchProviders.locationSelector.invalidLocation'))
      return false
    }

    return true
  }

  // Utility function to update Redux state and URL params
  const updateLocationState = newLocationData => {
    // Update location in Redux and URL params
    dispatch(
      Actions.createAction(Actions.SEARCH_BY_LOCATION_UPDATED, newLocationData)
    )

    // Update URL params without navigation
    const currentParams = getUrlParams()
    const updatedParams = {
      ...currentParams,
      ...newLocationData
    }
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${setUrlParams(updatedParams)}`
    )
  }

  const changeValidateLocation = input => {
    const trimmedValue = input?.trim() || ''
    let newLocationData = {
      city: null,
      state: null,
      zipcode: null
    }

    if (trimmedValue) {
      // If the value is a zipcode
      if (/^\d+$/.test(trimmedValue)) {
        const stateCode = validateZipcode(trimmedValue)
        newLocationData = {
          city: null,
          state: stateCode,
          zipcode: trimmedValue
        }
      } else {
        const matchingOption = locationOptions.find(option => {
          const rawValue = option.value
          const formattedValue = `${option.label}, ${
            option.value.split(',')[1]
          }`
          return (
            rawValue === trimmedValue ||
            rawValue.toLowerCase() === trimmedValue.toLowerCase() ||
            formattedValue === trimmedValue ||
            formattedValue.toLowerCase() === trimmedValue.toLowerCase()
          )
        })

        if (matchingOption) {
          const [city, state] = matchingOption.value.split(',')
          newLocationData = {
            city: city,
            state: state,
            zipcode: null
          }
        }
      }
    }

    updateLocationState(newLocationData)
  }

  const handleOk = overrideNewLocation => {
    const location =
      typeof overrideNewLocation === 'string'
        ? overrideNewLocation
        : newLocation

    if (isLocationValid(location)) {
      changeValidateLocation(location)
      setIsModalOpen(false)
      setNewLocation('')
      setValidationError('')
    }
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setValidationError('')
  }

  const handleClearClick = () => {
    setNewLocation('')
    setValidationError('')
    // Clear location but preserve state, falling back to the default state
    const newLocationData = {
      city: null,
      state: location.state || defaultState,
      zipcode: null
    }
    updateLocationState(newLocationData)
  }

  // Helper function to highlight matching text
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm || !text) return text

    const index = text.toLowerCase().indexOf(searchTerm.toLowerCase())
    if (index === -1) return text

    const beforeMatch = text.substring(0, index)
    const match = text.substring(index, index + searchTerm.length)
    const afterMatch = text.substring(index + searchTerm.length)

    return (
      <>
        {beforeMatch}
        <span style={{ fontWeight: 'bold' }}>{match}</span>
        {afterMatch}
      </>
    )
  }

  const handleInputChange = value => {
    setNewLocation(value)
    if (!value) {
      setValidationError('')
    }

    // Only show options if user has typed something
    if (value && value.trim().length > 0) {
      // If there's already a state selected, filter to only show cities from that state
      const optionsToFilter = location.state
        ? locationOptions.filter(
            option => option.value.split(',')[1] === location.state
          )
        : locationOptions

      const filtered = optionsToFilter
        .filter(
          option =>
            option.value.toLowerCase().includes(value.toLowerCase()) ||
            option.label.toLowerCase().includes(value.toLowerCase())
        )
        .map(option => {
          const displayText = `${option.label}, ${option.value.split(',')[1]}`
          return {
            ...option,
            value: option.value,
            label: highlightMatch(displayText, value)
          }
        })
      setFilteredOptions(filtered)
    } else {
      setFilteredOptions([])
    }
  }

  const handleSelect = value => {
    // Format the selected value to include space: "City, ST"
    const option = locationOptions.find(opt => opt.value === value)
    const formattedValue = option
      ? `${option.label}, ${option.value.split(',')[1]}`
      : value

    setNewLocation(formattedValue)

    if (isLocationValid(formattedValue)) {
      setValidationError('')
      handleOk(formattedValue)
    }
  }

  // Helper function to display location
  const getLocationDisplay = () => {
    if (location.city && location.state && location.zipcode) {
      return `${location.city}, ${location.state} ${location.zipcode}`
    } else if (location.city && location.state) {
      return `${location.city}, ${location.state}`
    } else if (location.zipcode && location.state) {
      return `${location.state} ${location.zipcode}`
    } else if (location.zipcode) {
      return location.zipcode
    } else if (location.state) {
      return location.state
    }
    return ''
  }

  const locationDisplay = getLocationDisplay()
  const stateOnly = location.state && !location.city && !location.zipcode

  const renderMobileListView = () => (
    <div className='location-suggestions-list'>
      {filteredOptions.map(option => {
        const [city, state] = option.value.split(',')
        const formattedValue = `${city}, ${state}`
        return (
          <div
            key={option.value}
            className='location-suggestion-item'
            onClick={() => {
              setNewLocation(formattedValue)
              if (!isZipcode) {
                handleOk(formattedValue)
              }
            }}
          >
            {option.label}
          </div>
        )
      })}
    </div>
  )

  return (
    <div className='location-selector'>
      <i className='icon-providers-location-selector' />
      {locationDisplay}
      {locationDisplay && (
        <div className='location-action-links'>
          {!stateOnly && (
            <span className='location-action-link' onClick={handleClearClick}>
              {$t('searchProviders.locationSelector.clear')}
            </span>
          )}
          <span className='location-action-link' onClick={handleChangeClick}>
            {stateOnly
              ? $t('searchProviders.locationSelector.enterLocation')
              : $t('searchProviders.locationSelector.change')}
          </span>
        </div>
      )}
      {!locationDisplay && (
        <span className='location-action-link' onClick={handleChangeClick}>
          {$t('searchProviders.locationSelector.enterLocation')}
        </span>
      )}
      <Dialog
        title={$t('searchProviders.locationSelector.enterLocation')}
        className='location-selector-dialog'
        isVisible={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        footerActions={
          !isMobile || !filteredOptions.length
            ? [
                <div key='footer' className='location-selector-dialog-footer'>
                  <Button type='primary' onClick={handleOk}>
                    {$t('common.apply')}
                  </Button>
                </div>
              ]
            : []
        }
      >
        <div className='location-modal-content'>
          <label className='location-input-label'>
            {$t('searchProviders.locationSelector.subtitle')}
          </label>
          <AutocompleteInput
            useSearchInput={false}
            renderOption={option => option.name}
            value={newLocation}
            options={filteredOptions}
            onInputChange={e => handleInputChange(e.target.value)}
            onSelect={handleSelect}
            onSearch={handleOk}
            placeholder={$t('searchProviders.locationSelector.placeholder')}
            allowClear={true}
            filterOption={false}
            status={validationError ? 'error' : ''}
            maxLength={100}
            renderOptionsList={renderMobileListView}
            viewMode={isMobile ? 'list' : 'dropdown'}
            shouldShowEmptyStateCondition={!isZipcode}
          />
          {validationError && (
            <div className='location-validation-error'>{validationError}</div>
          )}
          {/* TODO: Implement default location */}
          {/* <div className='default-location-checkbox'>
            <label>
              <input
                type='checkbox'
                checked={isDefaultLocation}
                onChange={e => setIsDefaultLocation(e.target.checked)}
              />
              Set this as my default location
            </label>
          </div> */}
        </div>
      </Dialog>
    </div>
  )
}

export default LocationSelector
